import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { funnelMilestone } from '../../api/funnel';

/**
 * Invite-a-friend dialogue — opens when the player taps the dashed "+"
 * invite seat on the wheel-room seat rail ({@code InviteAvatarButton}).
 *
 * <p>Producer brief (contextual-explainer pass): tapping the invite
 * seat should "open a dialogue with the details and explanation" rather
 * than firing the OS share sheet straight away. So this sheet explains
 * what the invite does first, then offers a single Share CTA that hands
 * off to {@code navigator.share} (with a clipboard-copy fallback).
 *
 * <p>This is also the front door to the viral-invite loop — the same
 * surface VI-4 share-and-measure builds on, so it lives as a standalone
 * component rather than inline in the seat rail.
 *
 * <p>A centred card with one line of copy and two big buttons (producer,
 * 2026-10-06: "less and larger text. Not at the bottom of the screen").
 * It was a bottom sheet with a paragraph of small print.
 *
 * <p>Rendered through a {@code createPortal} to {@code document.body}.
 * The seat rail lives inside the reel-race {@code .rr-scale} wrapper
 * (a {@code scale(0.9)} transform), which would otherwise trap a
 * {@code position: fixed} child in its stacking context — letting the
 * body-level item-burst floaters (z-12 portal) paint over the modal.
 * The portal lifts the sheet to the body stacking context at z-1000,
 * above the floaters, the PAUSED badge (100), and the wallet pill (45).
 */
export interface InviteFriendSheetProps {
  /** Room invite code. When null (e.g. a dev preview with no live
   *  room) the sheet still explains the feature but disables sharing. */
  inviteCode: string | null;
  /** Optional table name, shown in the explainer copy for warmth. */
  roomName?: string | null;
  /** Dismiss the sheet. */
  onClose: () => void;
}

export function InviteFriendSheet({
  inviteCode,
  roomName = null,
  onClose,
}: InviteFriendSheetProps) {
  const [flash, setFlash] = useState<string | null>(null);
  const url = inviteCode ? `https://goodtime.casino/r/${inviteCode}` : null;

  const showFlash = (msg: string): void => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 1600);
  };

  // Copy the invite link to the clipboard — the desktop-friendly path (the OS
  // share sheet is clunky there). HTTPS gives us the async Clipboard API; fall
  // back to the legacy textarea+execCommand if it's unavailable or denied.
  const copyLink = (): void => {
    if (!url) return;
    // Playtest funnel — copying the invite link is an invite too (the
    // desktop path, where there's often no native share sheet). Once-per-device.
    funnelMilestone('invite_sent');
    const done = (): void => showFlash('Invite link copied!');
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(url).then(done).catch(() => {
        legacyCopy(url);
        done();
      });
    } else {
      legacyCopy(url);
      done();
    }
  };

  const handleShare = (): void => {
    if (!url) return;
    // Playtest funnel — the player actually shared an invite. Once-per-device,
    // fire-and-forget; playerId is read from localStorage (none in scope here).
    funnelMilestone('invite_sent');
    const shareText = `Join my table 👉 ${url}`;
    if (navigator.share) {
      void navigator
        .share({ title: 'Goodtime', text: shareText, url })
        .then(() => showFlash('Shared!'))
        .catch(() => {
          /* user dismissed the OS share sheet — no flash */
        });
    } else if (navigator.clipboard?.writeText) {
      void navigator.clipboard
        .writeText(url)
        .then(() => showFlash('Link copied!'))
        .catch(() => showFlash('Link copied!'));
    } else {
      showFlash('Link copied!');
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="Invite a friend"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 360,
          background: '#1a0830',
          border: '2px solid rgba(252,211,77,0.6)',
          borderRadius: 22,
          padding: '26px 20px 20px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
          fontFamily: '"Fredoka", system-ui',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        {/* Small toast — copy / share confirmation, floats at the top of the
            card so it doesn't shift the layout. */}
        {flash ? (
          <div
            style={{
              position: 'absolute',
              top: -18,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.9)',
              color: '#FCE4A0',
              fontSize: 15,
              fontWeight: 700,
              padding: '8px 16px',
              borderRadius: 999,
              border: '1px solid rgba(252,211,77,0.5)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 2,
            }}
          >
            {flash}
          </div>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.08)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name="close" size={16} alt="" />
        </button>

        <h2
          style={{
            fontSize: 26,
            fontWeight: 900,
            color: '#FCE4A0',
            margin: 0,
          }}
        >
          👋 Invite a friend
        </h2>

        <p
          style={{
            margin: '10px 0 0',
            fontSize: 18,
            lineHeight: 1.4,
            color: 'rgba(255,255,255,0.88)',
          }}
        >
          They'll join you at{' '}
          {roomName ? <b style={{ color: '#fff' }}>{roomName}</b> : 'this table'}.
        </p>

        <button
          type="button"
          onClick={handleShare}
          disabled={!url}
          style={{
            marginTop: 22,
            width: '100%',
            borderRadius: 14,
            border: 'none',
            padding: '17px 16px',
            fontFamily: 'inherit',
            fontSize: 20,
            fontWeight: 800,
            cursor: url ? 'pointer' : 'not-allowed',
            background: url ? '#FBBF24' : 'rgba(255,255,255,0.1)',
            color: url ? '#1a0830' : 'rgba(255,255,255,0.4)',
            boxShadow: url ? '0 4px 0 #B7791F' : 'none',
          }}
        >
          Share invite link
        </button>

        {inviteCode ? (
          <button
            type="button"
            onClick={copyLink}
            aria-label="Copy invite link"
            style={{
              marginTop: 12,
              width: '100%',
              borderRadius: 14,
              border: '1.5px solid rgba(252,211,77,0.45)',
              background: 'transparent',
              padding: '13px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 17,
              fontWeight: 700,
              color: '#FCE4A0',
            }}
          >
            Copy link
            <span
              style={{
                fontWeight: 900,
                letterSpacing: 2,
                color: 'rgba(252,228,160,0.7)',
              }}
            >
              {inviteCode}
            </span>
          </button>
        ) : (
          <p
            style={{
              marginTop: 14,
              fontSize: 15,
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            Invite link unavailable in this preview.
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Legacy clipboard fallback for contexts without the async Clipboard API
 *  (older WebViews / denied permission) — a hidden textarea + execCommand. */
function legacyCopy(text: string): void {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  } catch {
    /* nothing more we can do — the toast still shows optimistically */
  }
}
